import { useMutation } from "@apollo/client"
import { Formik } from "formik"
import { ReactNode } from "react"
import useAuthContext from "state/useAuthContext"
import { SIGN_UP_VIA_EMAIL } from "utils/auth"
import * as Yup from "yup"
import { SignUpViaEmail } from "__generated__/SignUpViaEmail"
import useOnboardingModal from "components/Modals/OnboardingModal/hook"
import { FirstNameField } from "@pallet/components/fields/FirstName"
import { LastNameField } from "@pallet/components/fields/LastName"
import { EmailField } from "@pallet/components/fields/Email"

type FormProviderProps = {
    onSuccess: () => any
    children: ReactNode
}

const FormProvider = (props: FormProviderProps) => {
    const { refresh } = useAuthContext()
    const [signup] = useMutation<SignUpViaEmail>(SIGN_UP_VIA_EMAIL)
    const { email, password } = useOnboardingModal()

    return (
        <Formik
            initialValues={{
                firstName: "",
                lastName: "",
                email: email ?? "",
                password: password ?? "",
                confirmPassword: "",
            }}
            validationSchema={Yup.object().shape({
                ...FirstNameField.validators.required,
                ...LastNameField.validators.required,
                ...EmailField.validators.required,
                password: Yup.string()
                    .required("Required")
                    .min(8, "Password must be over 8 characters long"),
                confirmPassword: Yup.string()
                    .required("Required")
                    .oneOf(
                        [Yup.ref("password")],
                        "Your passwords do not match."
                    ),
            })}
            onSubmit={async (values, helpers) => {
                const { data } = await signup({
                    variables: {
                        email: values.email,
                        password: values.password,
                        firstName: values.firstName,
                        lastName: values.lastName,
                    },
                })
                if (data?.signUp?.validationErrors) {
                    return helpers.setErrors({
                        email: "Email already exists. Please try again.",
                    })
                }
                await refresh()
                if (props.onSuccess) props.onSuccess()
            }}
            {...props}
        />
    )
}

export default FormProvider
